import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  InventoryState,
  InventoryOverviewResponse,
  AllCustomerOrdersResponse,
  AllNeededProductsResponse,
  UpdatedProductsResponse,
  UpdateProductQuantityResponse,
  UpdateProductStatusResponse,
  GetEmailsResponse,
  AddEmailResponse,
  DeleteEmailResponse,
} from "../../types/inventory";
import {
  getInventoryOverview,
  getAllCustomerOrders,
  getAllNeededProducts,
  getUpdatedProducts,
  updateProductQuantity,
  updateProductStatus,
  updateAllProductsThreshold,
  updateProductThreshold,
  getEmails,
  addEmail,
  deleteEmail,
} from "../thunk";

const initialState: InventoryState = {
  inventoryOverview: {
    data: [],
    page: 1,
    totalPages: 0,
    totalProducts: 0,
    status: "idle",
    error: null,
  },
  customerOrders: {
    data: [],
    status: "idle",
    error: null,
  },
  neededProducts: {
    data: [],
    page: 1,
    limit: 10,
    totalPages: 0,
    totalProducts: 0,
    count: 0,
    status: "idle",
    error: null,
  },
  updatedProducts: {
    data: [],
    count: 0,
    page: 1,
    limit: 50,
    totalPages: 0,
    totalProducts: 0,
    status: "idle",
    error: null,
  },
  updateQuantity: {
    status: "idle",
    error: null,
  },
  updateProductStatus: {
    status: "idle",
    error: null,
  },
  emails: {
    data: [],
    count: 0,
    status: "idle",
    error: null,
  },
  addEmail: {
    status: "idle",
    error: null,
  },
  deleteEmail: {
    status: "idle",
    error: null,
  },
};

const inventorySlice = createSlice({
  name: "inventory",
  initialState,
  reducers: {
    resetUpdateQuantityStatus: (state) => {
      state.updateQuantity.status = "idle";
      state.updateQuantity.error = null;
    },
    resetUpdateProductStatusStatus: (state) => {
      state.updateProductStatus.status = "idle";
      state.updateProductStatus.error = null;
    },
    resetAddEmailStatus: (state) => {
      state.addEmail.status = "idle";
      state.addEmail.error = null;
    },
    resetDeleteEmailStatus: (state) => {
      state.deleteEmail.status = "idle";
      state.deleteEmail.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Inventory Overview
      .addCase(getInventoryOverview.pending, (state) => {
        state.inventoryOverview.status = "loading";
        state.inventoryOverview.error = null;
      })
      .addCase(
        getInventoryOverview.fulfilled,
        (state, action: PayloadAction<InventoryOverviewResponse>) => {
          state.inventoryOverview.status = "success";
          state.inventoryOverview.data = action.payload.data;
          state.inventoryOverview.page = action.payload.page;
          state.inventoryOverview.totalPages = action.payload.totalPages;
          state.inventoryOverview.totalProducts = action.payload.totalProducts;
          state.inventoryOverview.error = null;
        }
      )
      .addCase(getInventoryOverview.rejected, (state, action) => {
        state.inventoryOverview.status = "failed";
        state.inventoryOverview.error =
          action.error?.message || "Failed to fetch inventory overview";
      })

      // Customer Orders
      .addCase(getAllCustomerOrders.pending, (state) => {
        state.customerOrders.status = "loading";
        state.customerOrders.error = null;
      })
      .addCase(
        getAllCustomerOrders.fulfilled,
        (state, action: PayloadAction<AllCustomerOrdersResponse>) => {
          state.customerOrders.status = "success";
          state.customerOrders.data = action.payload.data;
          state.customerOrders.error = null;
        }
      )
      .addCase(getAllCustomerOrders.rejected, (state, action) => {
        state.customerOrders.status = "failed";
        state.customerOrders.error =
          action.error?.message || "Failed to fetch customer orders";
      })

      // Needed Products
      .addCase(getAllNeededProducts.pending, (state) => {
        state.neededProducts.status = "loading";
        state.neededProducts.error = null;
      })
      .addCase(
        getAllNeededProducts.fulfilled,
        (state, action: PayloadAction<AllNeededProductsResponse>) => {
          state.neededProducts.status = "success";
          state.neededProducts.data = action.payload.data;
          state.neededProducts.page = action.payload.page;
          state.neededProducts.limit = action.payload.limit;
          state.neededProducts.totalPages = action.payload.totalPages;
          state.neededProducts.totalProducts = action.payload.totalProducts;
          state.neededProducts.count = action.payload.count;
          state.neededProducts.error = null;
        }
      )
      .addCase(getAllNeededProducts.rejected, (state, action) => {
        state.neededProducts.status = "failed";
        state.neededProducts.error =
          action.error?.message || "Failed to fetch needed products";
      })

      // Updated Products
      .addCase(getUpdatedProducts.pending, (state) => {
        state.updatedProducts.status = "loading";
        state.updatedProducts.error = null;
      })
      .addCase(
        getUpdatedProducts.fulfilled,
        (state, action: PayloadAction<UpdatedProductsResponse>) => {
          state.updatedProducts.status = "success";
          state.updatedProducts.data = action.payload.products;
          state.updatedProducts.count = action.payload.count;
          state.updatedProducts.page = action.payload.page;
          state.updatedProducts.limit = action.payload.limit;
          state.updatedProducts.totalPages = action.payload.totalPages;
          state.updatedProducts.totalProducts = action.payload.totalProducts;
          state.updatedProducts.error = null;
        }
      )
      .addCase(getUpdatedProducts.rejected, (state, action) => {
        state.updatedProducts.status = "failed";
        state.updatedProducts.error =
          action.error?.message || "Failed to fetch updated products";
      })

      // Update Product Quantity
      .addCase(updateProductQuantity.pending, (state) => {
        state.updateQuantity.status = "loading";
        state.updateQuantity.error = null;
      })
      .addCase(
        updateProductQuantity.fulfilled,
        (state, action: PayloadAction<UpdateProductQuantityResponse>) => {
          state.updateQuantity.status = "success";
          state.updateQuantity.error = null;
        }
      )
      .addCase(updateProductQuantity.rejected, (state, action) => {
        state.updateQuantity.status = "failed";
        state.updateQuantity.error =
          action.error?.message || "Failed to update product quantity";
      })

      // Update Product Status
      .addCase(updateProductStatus.pending, (state) => {
        state.updateProductStatus.status = "loading";
        state.updateProductStatus.error = null;
      })
      .addCase(
        updateProductStatus.fulfilled,
        (state, action: PayloadAction<UpdateProductStatusResponse>) => {
          state.updateProductStatus.status = "success";
          state.updateProductStatus.error = null;
          
          console.log('Update product status response:', action.payload);
          
          // Update the product in the inventory overview with the new isAvailable status
          if (action.payload.data && action.payload.data._id) {
            const productIndex = state.inventoryOverview.data.findIndex(
              (p) => p.product_id === action.payload.data._id
            );
            console.log('Product index found:', productIndex, 'isAvailable:', action.payload.data.isAvailable);
            if (productIndex !== -1) {
              state.inventoryOverview.data[productIndex].isAvailable = 
                action.payload.data.isAvailable;
            }
          }
        }
      )
      .addCase(updateProductStatus.rejected, (state, action) => {
        state.updateProductStatus.status = "failed";
        state.updateProductStatus.error =
          action.error?.message || "Failed to update product status";
      })

      // Get Emails
      .addCase(getEmails.pending, (state) => {
        state.emails.status = "loading";
        state.emails.error = null;
      })
      .addCase(
        getEmails.fulfilled,
        (state, action: PayloadAction<GetEmailsResponse>) => {
          state.emails.status = "success";
          state.emails.data = action.payload.data;
          state.emails.count = action.payload.count;
          state.emails.error = null;
        }
      )
      .addCase(getEmails.rejected, (state, action) => {
        state.emails.status = "failed";
        state.emails.error =
          action.error?.message || "Failed to fetch emails";
      })

      // Add Email
      .addCase(addEmail.pending, (state) => {
        state.addEmail.status = "loading";
        state.addEmail.error = null;
      })
      .addCase(
        addEmail.fulfilled,
        (state, action: PayloadAction<AddEmailResponse>) => {
          state.addEmail.status = "success";
          // Update the emails list with the new data
          if (action.payload.data && action.payload.data.emails) {
            state.emails.data = action.payload.data.emails;
            state.emails.count = action.payload.data.emails.length;
          }
          state.addEmail.error = null;
        }
      )
      .addCase(addEmail.rejected, (state, action) => {
        state.addEmail.status = "failed";
        state.addEmail.error =
          action.error?.message || "Failed to add email";
      })

      // Delete Email
      .addCase(deleteEmail.pending, (state) => {
        state.deleteEmail.status = "loading";
        state.deleteEmail.error = null;
      })
      .addCase(
        deleteEmail.fulfilled,
        (state, action: PayloadAction<DeleteEmailResponse>) => {
          state.deleteEmail.status = "success";
          state.deleteEmail.error = null;
        }
      )
      .addCase(deleteEmail.rejected, (state, action) => {
        state.deleteEmail.status = "failed";
        state.deleteEmail.error =
          action.error?.message || "Failed to delete email";
      })

      // Update All Products Threshold — persist threshold on every loaded product
      .addCase(updateAllProductsThreshold.fulfilled, (state, action) => {
        const payload = action.payload as
          | { threshold?: number; product_threshold?: number }
          | undefined;
        const threshold = payload?.product_threshold ?? payload?.threshold;
        if (typeof threshold === "number" && Number.isFinite(threshold)) {
          state.inventoryOverview.data = state.inventoryOverview.data.map(
            (p) => ({ ...p, threshold, product_threshold: threshold }),
          );
        }
      })

      // Update Single Product Threshold — persist threshold on the targeted product
      .addCase(updateProductThreshold.fulfilled, (state, action) => {
        const payload = action.payload as
          | { productId?: string; threshold?: number; product_threshold?: number }
          | undefined;
        const { productId } = payload ?? {};
        const threshold = payload?.product_threshold ?? payload?.threshold;
        if (productId && typeof threshold === "number" && Number.isFinite(threshold)) {
          const idx = state.inventoryOverview.data.findIndex(
            (p) => p.product_id === productId,
          );
          if (idx !== -1) {
            state.inventoryOverview.data[idx] = {
              ...state.inventoryOverview.data[idx],
              threshold,
              product_threshold: threshold,
            };
          }
        }
      });
  },
});

export const { resetUpdateQuantityStatus, resetUpdateProductStatusStatus, resetAddEmailStatus, resetDeleteEmailStatus } = inventorySlice.actions;
export default inventorySlice.reducer;
